import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { CheckIcon, SparkleIcon } from './icons/OrganicIcons';

const RegistrationForm = () => {
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    cargo: '',
    relacionTecnologia: '',
    dispositivos: [],
    herramientas: [],
    mejoras: [],
    motivacion: '',
    quantity: 1,
    isCompany: false,
    company: '',
    // UTM tracking (hidden)
    utmSource: '',
    utmMedium: '',
    utmCampaign: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [progress, setProgress] = useState(0);
  const [mounted, setMounted] = useState(false);
  const [focusedField, setFocusedField] = useState('');
  const [pricePreview, setPricePreview] = useState(null);
  const errorTopRef = useRef(null);

  // Safely read and format total price from different response shapes
  const getTotalPrice = () => {
    const maybe = (pricePreview && (pricePreview.totalPrice ?? pricePreview?.data?.totalPrice)) ?? 0;
    const num = typeof maybe === 'number' ? maybe : Number(maybe);
    return Number.isFinite(num) ? num : 0;
  };

  useEffect(() => {
    setMounted(true);
    // Capture UTM params from URL once on mount
    try {
      const params = new URLSearchParams(window.location.search);
      const utmSource = params.get('utm_source') || '';
      const utmMedium = params.get('utm_medium') || '';
      const utmCampaign = params.get('utm_campaign') || '';
      if (utmSource || utmMedium || utmCampaign) {
        setFormData(prev => ({ ...prev, utmSource, utmMedium, utmCampaign }));
      }
    } catch (_) { /* noop */ }
  }, []);

  const dispositivosOptions = [
    'Notebook',
    'PC de escritorio', 
    'Tablet',
    'Celular',
    'Otro'
  ];

  const herramientasOptions = [
    'Google Workspace',
    'Microsoft Office',
    'WhatsApp',
    'Herramientas de diseño',
    'Herramientas de IA',
    'Otra'
  ];

  const mejorasOptions = [
    'Organizar y simplificar mi trabajo',
    'Usar IA de forma más eficiente',
    'Mejorar comunicación y presentación de ideas',
    'Ahorrar tiempo automatizando tareas',
    'Otro'
  ];

  // Campos de texto libre cuando se elige Otro/Otra
  const [otroDispositivo, setOtroDispositivo] = useState('');
  const [otraHerramienta, setOtraHerramienta] = useState('');
  const [otraMejora, setOtraMejora] = useState('');

  const upsertOtherInArray = (arr, label, value) => {
    const filtered = arr.filter((v) => !String(v).toLowerCase().startsWith(`${label.toLowerCase()}:`));
    if (!value || value.trim().length === 0) return filtered;
    return [...filtered, `${label}: ${value.trim()}`];
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Track field interaction
    if (window.gtag) {
      window.gtag('event', 'form_field_interaction', {
        'event_category': 'Form',
        'event_label': name,
        'value': type === 'checkbox' ? checked : 1
      });
    }
    
    if (type === 'checkbox') {
      const currentArray = formData[name] || [];
      if (checked) {
        setFormData(prev => ({
          ...prev,
          [name]: [...currentArray, value]
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [name]: currentArray.filter(item => item !== value)
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Update progress
    setTimeout(() => {
      let checks = [];
      if (formData.isCompany) {
        const count = Math.min(Math.max(Number(formData.quantity) || 1, 1), 5);
        for (let i = 1; i <= count; i++) {
          checks.push(Boolean(String(formData[`p${i}_nombre`] || '').trim()));
          checks.push(/\S+@\S+\.\S+/.test(String(formData[`p${i}_email`] || '')));
          checks.push(Boolean(String(formData[`p${i}_telefono`] || '').trim()));
          checks.push(Boolean(String(formData[`p${i}_cargo`] || '').trim()));
        }
        checks.push(Boolean(formData.company.trim()));
      } else {
        checks = [
        !!formData.nombre.trim(),
        /\S+@\S+\.\S+/.test(formData.email),
        !!formData.telefono.trim(),
        !!formData.cargo.trim(),
        ];
      }
      checks.push(!!formData.relacionTecnologia);
      checks.push(formData.dispositivos.length > 0);
      checks.push(true); // herramientas opcional
      checks.push(true); // mejoras opcional
      checks.push(true); // motivación opcional
      checks.push(!!formData.quantity && formData.quantity >= 1 && formData.quantity <= 10);
      const filled = checks.filter(Boolean).length;
      setProgress(Math.round((filled / checks.length) * 100));
    }, 0);
  };

  const validateForm = () => {
    const newErrors = {};

    // Si NO es empresa, validar datos personales base
    if (!formData.isCompany) {
    if (!formData.nombre.trim()) newErrors.nombre = 'El nombre es requerido';
    if (!formData.email.trim()) {
      newErrors.email = 'El email es requerido';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'El email no es válido';
    }
    if (!formData.telefono.trim()) newErrors.telefono = 'El teléfono es requerido';
      if (!formData.cargo.trim()) newErrors.cargo = 'El cargo es requerido';
    }
    if (!formData.relacionTecnologia) newErrors.relacionTecnologia = 'Debes seleccionar una opción';
    if (formData.dispositivos.length === 0) newErrors.dispositivos = 'Debes seleccionar al menos un dispositivo';
    // motivación ahora opcional
    
    // Validate new fields
    if (!formData.quantity || formData.quantity < 1 || formData.quantity > 5) {
      newErrors.quantity = 'La cantidad debe ser entre 1 y 5 personas';
    }
    if (formData.isCompany && !formData.company.trim()) {
      newErrors.company = 'El nombre de la empresa es requerido';
    }

    // Validar campos dinámicos de participantes adicionales (solo empresas)
    if (formData.isCompany) {
      const count = Math.min(Math.max(Number(formData.quantity) || 1, 1), 5);
      for (let i = 1; i <= count; i++) {
        const n = String(formData[`p${i}_nombre`] || '').trim();
        const e = String(formData[`p${i}_email`] || '').trim();
        const t = String(formData[`p${i}_telefono`] || '').trim();
        const c = String(formData[`p${i}_cargo`] || '').trim();
        if (!n) newErrors[`p${i}_nombre`] = 'Requerido';
        if (!e) newErrors[`p${i}_email`] = 'Requerido';
        else if (!/\S+@\S+\.\S+/.test(e)) newErrors[`p${i}_email`] = 'Email inválido';
        if (!t) newErrors[`p${i}_telefono`] = 'Requerido';
        if (!c) newErrors[`p${i}_cargo`] = 'Requerido';
      }
    }

    setErrors(newErrors);
    const keys = Object.keys(newErrors);
    if (keys.length > 0) {
      // scroll al primer error
      const first = keys[0];
      setTimeout(() => {
        const el = document.querySelector(`[name="${first}"]`);
        if (el && el.scrollIntoView) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          if (el.focus) el.focus();
        }
      }, 0);
      return false;
    }
    return true;
  };

  const [quotaFull, setQuotaFull] = useState(false);

  useEffect(() => {
    checkQuota();
  }, []);

  const checkQuota = async () => {
    try {
      const response = await axios.get('/.netlify/functions/checkQuota');
      if (response.data.success) {
        setQuotaFull(response.data.data.isFull);
        
        // Track quota status
        if (window.gtag) {
          window.gtag('event', 'check_quota', {
            'event_category': 'Quota',
            'event_label': 'Quota Status',
            'value': response.data.data.percentage
          });
        }
      }
    } catch (error) {
      console.error('Error checking quota:', error);
    }
  };

  const getPricePreview = useCallback(async () => {
    try {
      const response = await axios.post('/.netlify/functions/pricePreview', {
        quantity: formData.quantity,
        isCompany: formData.isCompany
      });
      // asegurar forma plana
      const data = response.data && response.data.data ? response.data.data : response.data;
      setPricePreview(data);
    } catch (error) {
      console.error('Error getting price preview:', error);
      setPricePreview(null);
    }
  }, [formData.quantity, formData.isCompany]);

  // Update price preview when quantity or isCompany changes
  useEffect(() => {
    // Si NO es empresa, cantidad fija en 1
    if (!formData.isCompany && formData.quantity !== 1) {
      setFormData((prev) => ({ ...prev, quantity: 1 }));
      return;
    }
    if (formData.quantity > 0) {
      getPricePreview();
    }
  }, [formData.quantity, formData.isCompany, getPricePreview]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      // Mensaje general de validación en el banner superior
      setErrors(prev => ({ ...prev, submit: 'Por favor, revisá los campos marcados.' }));
      // Llevar al banner de error
      setTimeout(() => { errorTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 0);
      return;
    }

    if (quotaFull) {
      setErrors({ submit: 'Lo sentimos, el cupo está lleno. Te sugerimos intentar en la próxima edición.' });
      setTimeout(() => { errorTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 0);
      return;
    }

    setIsSubmitting(true);

    try {
      // Submit directly to createPreference function
      // Mapear datos base: si es empresa usar p1_* como contacto principal
      const primaryNombre = formData.isCompany ? (formData.p1_nombre || formData.nombre) : formData.nombre;
      const primaryEmail = formData.isCompany ? (formData.p1_email || formData.email) : formData.email;
      const primaryTel = formData.isCompany ? (formData.p1_telefono || formData.telefono) : formData.telefono;
      const primaryCargo = formData.isCompany ? (formData.p1_cargo || formData.cargo) : formData.cargo;

      // Incluir datos de todos los participantes cuando es empresa
      const extraParticipants = {};
      if (formData.isCompany) {
        const count = Math.min(Math.max(Number(formData.quantity) || 1, 1), 5);
        for (let i = 1; i <= count; i++) {
          extraParticipants[`p${i}_nombre`] = formData[`p${i}_nombre`] || (i === 1 ? formData.nombre : '');
          extraParticipants[`p${i}_email`] = formData[`p${i}_email`] || (i === 1 ? formData.email : '');
          extraParticipants[`p${i}_telefono`] = formData[`p${i}_telefono`] || (i === 1 ? formData.telefono : '');
          extraParticipants[`p${i}_cargo`] = formData[`p${i}_cargo`] || (i === 1 ? formData.cargo : '');
        }
      }

      const response = await axios.post('/.netlify/functions/createPreference', {
        nombre: primaryNombre,
        email: primaryEmail,
        telefono: primaryTel,
        tipo: formData.tipo,
        empresa: formData.empresa,
        cargo: primaryCargo,
        relacionTecnologia: formData.relacionTecnologia,
        dispositivos: formData.dispositivos,
        herramientas: formData.herramientas,
        mejoras: formData.mejoras,
        motivacion: formData.motivacion,
        quantity: formData.quantity,
        isCompany: formData.isCompany,
        company: formData.company,
        utmSource: formData.utmSource,
        utmMedium: formData.utmMedium,
        utmCampaign: formData.utmCampaign,
        ...extraParticipants
      });
      
      if (response.data.waitlist) {
        // Show waitlist message
        setSubmitSuccess(true);
      } else if (response.data.init_point) {
        // Disparar verificación en background y guardar info mínima antes de redirigir a MP
        try {
          const expectedTotal = getTotalPrice();
          // Fire-and-forget: no esperamos la respuesta
          fetch('/.netlify/functions/verifyPayment-background', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            // Usar email del Participante 1 cuando es empresa
            body: JSON.stringify({ email: primaryEmail, expectedTotal })
          }).catch(() => {});
          localStorage.setItem('guia_lastPay', JSON.stringify({
            email: primaryEmail,
            expectedTotal
          }));
        } catch (_) { /* noop */ }
        // Redirigir a confirmación de inscripción (no mostramos link de pago)
        window.location.href = '/inscripcion-enviada';
      } else {
        throw new Error('Respuesta inesperada del servidor');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      if (error.response && error.response.status === 409) {
        setErrors({ submit: 'El email ingresado ya está registrado. Si necesitás ayuda, contáctanos.' });
      } else if (error.response) {
        setErrors({ submit: 'Tuvimos un problema del lado del servidor. Intentá nuevamente en unos minutos o contáctanos.' });
      } else {
        setErrors({ submit: 'No pudimos conectar con el servidor. Verificá tu conexión e intentá de nuevo.' });
      }
      setTimeout(() => { errorTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 0);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitSuccess) {
    return (
      <div className={`relative overflow-hidden rounded-[2rem] bg-white/80 backdrop-blur-xl border border-white/50 shadow-2xl p-12 text-center animate-scale-in ${mounted ? 'opacity-100' : 'opacity-0'}`}>
        <div className="space-y-8">
          <div className="flex justify-center">
            <div className="w-24 h-24 bg-gradient-to-br from-green-400 to-blue-400 rounded-3xl flex items-center justify-center shadow-2xl animate-bounce-gentle">
              <CheckIcon className="w-12 h-12 text-white" />
            </div>
          </div>
          <div className="space-y-4">
            <h3 className="text-3xl font-light text-neutral-700 leading-tight" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>
              ¡Inscripción exitosa!
            </h3>
            <p className="text-lg text-neutral-600 leading-relaxed max-w-md mx-auto" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>
              Gracias por inscribirte en INICIA. Te hemos enviado un email de confirmación con los próximos pasos.
            </p>
          </div>
          <button
            onClick={() => setSubmitSuccess(false)}
            className="px-8 py-4 bg-gradient-to-r from-green-400 to-blue-400 text-white font-medium rounded-2xl shadow-soft hover:shadow-medium transition-all duration-300 transform hover:scale-105"
            style={{ fontFamily: 'Poppins, Inter, sans-serif' }}
          >
            Inscribir otra persona
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={`space-y-12 animate-fade-in-up ${mounted ? 'opacity-100' : 'opacity-0'}`}>
      {/* Progress Bar */}
      <div className="w-full bg-white/40 backdrop-blur-sm rounded-full h-2 overflow-hidden">
        <div className="h-2 bg-gradient-to-r from-green-400 to-blue-400 transition-all duration-300" style={{ width: `${progress}%` }} />
      </div>
      {(errors.submit || Object.keys(errors).some(k => k !== 'submit')) && (
        <div ref={errorTopRef} className="bg-red-50/80 backdrop-blur-sm border border-red-200 text-red-700 px-6 py-4 rounded-2xl shadow-soft">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-red-400 rounded-full"></div>
            <span style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>{errors.submit || 'Por favor, revisá los campos marcados.'}</span>
          </div>
        </div>
      )}

      {/* Personal Information */}
      <div className="relative overflow-hidden rounded-[2rem] bg-white/80 backdrop-blur-xl border border-white/50 shadow-2xl p-8">
        <div className="flex items-center space-x-3 mb-8">
          <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-blue-400 rounded-2xl flex items-center justify-center shadow-soft">
            <SparkleIcon className="w-6 h-6 text-white" />
          </div>
          <h3 className="text-2xl font-light text-neutral-700" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>
            Información Personal
          </h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Primero: ¿Es empresa? y Cantidad */}
          <div className="space-y-2 md:col-span-1">
            <label className="block text-sm font-medium text-neutral-700" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>
              ¿Es para una empresa?
            </label>
            <div className="flex space-x-4">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="isCompany"
                  value="true"
                  checked={formData.isCompany === true}
                  onChange={() => setFormData(prev => ({ ...prev, isCompany: true }))}
                  className="w-4 h-4 text-green-400 focus:ring-green-400"
                />
                <span style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>Sí</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="isCompany"
                  value="false"
                  checked={formData.isCompany === false}
                  onChange={() => setFormData(prev => ({ ...prev, isCompany: false, quantity: 1 }))}
                  className="w-4 h-4 text-green-400 focus:ring-green-400"
                />
                <span style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>No</span>
              </label>
            </div>
          </div>

          {/* Cantidad (solo si es empresa) */}
          {formData.isCompany && (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-neutral-700" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>
              Cantidad de participantes de la misma empresa *
            </label>
            <select
              name="quantity"
              value={formData.quantity}
              onChange={(e) => setFormData(prev => ({ ...prev, quantity: Number(e.target.value) }))}
              onFocus={() => setFocusedField('quantity')}
              onBlur={() => setFocusedField('')}
              className={`w-full px-4 py-3 bg-white/60 backdrop-blur-sm border rounded-xl text-neutral-700 focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent transition-all duration-300 ${
                errors.quantity ? 'border-red-300 bg-red-50/50' : focusedField === 'quantity' ? 'border-green-400 shadow-soft' : 'border-white/30'
              }`}
            >
              {[1,2,3,4,5].map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            {errors.quantity && (
              <p className="text-red-500 text-sm flex items-center space-x-1" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>
                <span className="w-1 h-1 bg-red-400 rounded-full"></span>
                <span>{errors.quantity}</span>
              </p>
            )}
            {formData.isCompany && (
              <p className="text-sm text-neutral-600">
                Las empresas que inscriban más de dos participantes acceden a un precio preferencial del 50% para cada persona adicional.
              </p>
            )}
          </div>
          )}

          {formData.isCompany && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-neutral-700" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>
                Nombre de la empresa
              </label>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                onFocus={() => setFocusedField('company')}
                onBlur={() => setFocusedField('')}
                className={`w-full px-4 py-3 bg-white/60 backdrop-blur-sm border rounded-xl text-neutral-700 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent transition-all duration-300 ${
                  errors.company ? 'border-red-300 bg-red-50/50' : focusedField === 'company' ? 'border-green-400 shadow-soft' : 'border-white/30'
                }`}
                placeholder="Nombre de la empresa"
                style={{ fontFamily: 'Poppins, Inter, sans-serif' }}
              />
              {errors.company && (
                <p className="text-red-500 text-sm flex items-center space-x-1" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>
                  <span className="w-1 h-1 bg-red-400 rounded-full"></span>
                  <span>{errors.company}</span>
                </p>
              )}
            </div>
          )}



          {/* Price Preview (mostrar para ambos casos; si no es empresa siempre es 1 persona) */}
          {pricePreview && (
            <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-xl p-4">
              <div className="text-center space-y-2">
                {formData.isCompany ? (
                  <p className="text-sm text-neutral-600" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>
                    Vas a pagar {formData.quantity} {formData.quantity === 1 ? 'lugar' : 'lugares'}
                  </p>
                ) : (
                  <p className="text-sm text-neutral-600" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>
                    Precio por persona
                  </p>
                )}

                                  {formData.isCompany && formData.quantity >= 3 ? (
                    <>
                      <div className="text-sm text-neutral-600" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>
                        Descuento exclusivo para empresas: a partir de 3 personas, cada adicional abona AR$90.000.
                      </div>
                      <p className="text-lg font-semibold text-neutral-700" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>
                        Total: ${getTotalPrice().toLocaleString('es-AR')} ARS
                      </p>
                    </>
                  ) : (
                  <p className="text-lg font-semibold text-neutral-700" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>
                    Total: ${getTotalPrice().toLocaleString('es-AR')} ARS
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Campos de participantes (empresa) o datos base (individual) */}
          {formData.isCompany ? (
            <div className="md:col-span-2 space-y-8">
              <div className="px-4 py-3 bg-blue-50 border border-blue-200 rounded-xl text-sm text-neutral-700">
                El link de pago se enviará al correo electrónico del Participante 1.
              </div>
              {Array.from({ length: Math.min(Math.max(Number(formData.quantity) || 1, 1), 5) }).map((_, idx) => {
                const i = idx + 1;
                return (
                  <div key={`participant-${i}`} className="grid grid-cols-1 md:grid-cols-2 gap-8 p-4 bg-white/50 rounded-xl border border-white/30">
                    <div className="md:col-span-2">
                      <h4 className="text-neutral-800 font-medium">{`Participante ${i}`}</h4>
                    </div>
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-neutral-700" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>
                        Nombre y apellido *
                      </label>
                      <input
                        type="text"
                        name={`p${i}_nombre`}
                        value={formData[`p${i}_nombre`] || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, [`p${i}_nombre`]: e.target.value }))}
                        className={`w-full px-4 py-3 bg-white/60 backdrop-blur-sm border rounded-xl text-neutral-700 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent ${errors[`p${i}_nombre`] ? 'border-red-300 bg-red-50/50' : 'border-white/30'}`}
                        placeholder={`Nombre y apellido`}
                      />
                      {errors[`p${i}_nombre`] && (
                        <p className="text-red-500 text-sm">{errors[`p${i}_nombre`]}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-neutral-700" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>
                        Correo electrónico *
                      </label>
                      <input
                        type="email"
                        name={`p${i}_email`}
                        value={formData[`p${i}_email`] || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, [`p${i}_email`]: e.target.value }))}
                        className={`w-full px-4 py-3 bg-white/60 backdrop-blur-sm border rounded-xl text-neutral-700 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent ${errors[`p${i}_email`] ? 'border-red-300 bg-red-50/50' : 'border-white/30'}`}
                        placeholder={`Correo electrónico`}
                      />
                      {errors[`p${i}_email`] && (
                        <p className="text-red-500 text-sm">{errors[`p${i}_email`]}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-neutral-700" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>
                        Teléfono *
                      </label>
                      <input
                        type="tel"
                        name={`p${i}_telefono`}
                        value={formData[`p${i}_telefono`] || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, [`p${i}_telefono`]: e.target.value }))}
                        className={`w-full px-4 py-3 bg-white/60 backdrop-blur-sm border rounded-xl text-neutral-700 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent ${errors[`p${i}_telefono`] ? 'border-red-300 bg-red-50/50' : 'border-white/30'}`}
                        placeholder={`Teléfono`}
                      />
                      {errors[`p${i}_telefono`] && (
                        <p className="text-red-500 text-sm">{errors[`p${i}_telefono`]}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-neutral-700" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>
                        Cargo o rol *
                      </label>
                      <input
                        type="text"
                        name={`p${i}_cargo`}
                        value={formData[`p${i}_cargo`] || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, [`p${i}_cargo`]: e.target.value }))}
                        className={`w-full px-4 py-3 bg-white/60 backdrop-blur-sm border rounded-xl text-neutral-700 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent ${errors[`p${i}_cargo`] ? 'border-red-300 bg-red-50/50' : 'border-white/30'}`}
                        placeholder={`Cargo o rol`}
                      />
                      {errors[`p${i}_cargo`] && (
                        <p className="text-red-500 text-sm">{errors[`p${i}_cargo`]}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <>
              {/* Datos base para inscripción individual */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-neutral-700" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>
                  Nombre y Apellido *
            </label>
            <input
              type="text"
              name="nombre"
              value={formData.nombre}
              onChange={handleInputChange}
              onFocus={() => setFocusedField('nombre')}
              onBlur={() => setFocusedField('')}
              className={`w-full px-4 py-3 bg-white/60 backdrop-blur-sm border rounded-xl text-neutral-700 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent transition-all duration-300 ${
                errors.nombre ? 'border-red-300 bg-red-50/50' : focusedField === 'nombre' ? 'border-green-400 shadow-soft' : 'border-white/30'
              }`}
              placeholder="Tu nombre completo"
              style={{ fontFamily: 'Poppins, Inter, sans-serif' }}
            />
            {errors.nombre && (
              <p className="text-red-500 text-sm flex items-center space-x-1" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>
                <span className="w-1 h-1 bg-red-400 rounded-full"></span>
                <span>{errors.nombre}</span>
              </p>
            )}
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-neutral-700" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>
              Email *
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              onFocus={() => setFocusedField('email')}
              onBlur={() => setFocusedField('')}
              className={`w-full px-4 py-3 bg-white/60 backdrop-blur-sm border rounded-xl text-neutral-700 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent transition-all duration-300 ${
                errors.email ? 'border-red-300 bg-red-50/50' : focusedField === 'email' ? 'border-green-400 shadow-soft' : 'border-white/30'
              }`}
              placeholder="tu@email.com"
              style={{ fontFamily: 'Poppins, Inter, sans-serif' }}
            />
            {errors.email && (
              <p className="text-red-500 text-sm flex items-center space-x-1" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>
                <span className="w-1 h-1 bg-red-400 rounded-full"></span>
                <span>{errors.email}</span>
              </p>
            )}
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-neutral-700" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>
              Teléfono / WhatsApp *
            </label>
            <input
              type="tel"
              name="telefono"
              value={formData.telefono}
              onChange={handleInputChange}
              onFocus={() => setFocusedField('telefono')}
              onBlur={() => setFocusedField('')}
              className={`w-full px-4 py-3 bg-white/60 backdrop-blur-sm border rounded-xl text-neutral-700 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent transition-all duration-300 ${
                errors.telefono ? 'border-red-300 bg-red-50/50' : focusedField === 'telefono' ? 'border-green-400 shadow-soft' : 'border-white/30'
              }`}
              placeholder="+54 9 11 1234-5678"
              style={{ fontFamily: 'Poppins, Inter, sans-serif' }}
            />
            {errors.telefono && (
              <p className="text-red-500 text-sm flex items-center space-x-1" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>
                <span className="w-1 h-1 bg-red-400 rounded-full"></span>
                <span>{errors.telefono}</span>
              </p>
            )}
          </div>

              <div className="space-y-2 md:col-span-2">
            <label className="block text-sm font-medium text-neutral-700" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>
              Cargo o rol actual *
            </label>
            <input
              type="text"
              name="cargo"
              value={formData.cargo}
              onChange={handleInputChange}
              onFocus={() => setFocusedField('cargo')}
              onBlur={() => setFocusedField('')}
              className={`w-full px-4 py-3 bg-white/60 backdrop-blur-sm border rounded-xl text-neutral-700 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent transition-all duration-300 ${
                errors.cargo ? 'border-red-300 bg-red-50/50' : focusedField === 'cargo' ? 'border-green-400 shadow-soft' : 'border-white/30'
              }`}
              placeholder="Tu cargo o rol"
              style={{ fontFamily: 'Poppins, Inter, sans-serif' }}
            />
            {errors.cargo && (
              <p className="text-red-500 text-sm flex items-center space-x-1" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>
                <span className="w-1 h-1 bg-red-400 rounded-full"></span>
                <span>{errors.cargo}</span>
              </p>
            )}
          </div>
            </>
          )}
        </div>
      </div>

      {/* Technology Information */}
      <div className="relative overflow-hidden rounded-[2rem] bg-white/80 backdrop-blur-xl border border-white/50 shadow-2xl p-8">
        <div className="flex items-center space-x-3 mb-8">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-purple-400 rounded-2xl flex items-center justify-center shadow-soft">
            <SparkleIcon className="w-6 h-6 text-white" />
          </div>
          <h3 className="text-2xl font-light text-neutral-700" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>
            Relación con la Tecnología
          </h3>
        </div>
        {/* Nota aclaratoria al inicio del diagnóstico (solo empresas) */}
        {formData.isCompany && (
          <div className="mb-6">
            <div className="px-4 py-3 bg-gradient-to-r from-yellow-50 to-yellow-100 border border-yellow-200 rounded-xl text-sm text-neutral-700">
              A continuación, respondé las preguntas de diagnóstico pensando en tu empresa.
            </div>
          </div>
        )}
        
        <div className="space-y-6">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-neutral-700" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>
              ¿Cómo describirías tu relación con la tecnología hoy? *
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                'Somos mejores amigos',
                'Nos llevamos bien',
                'Es complicada',
                'Es un mundo nuevo para mí'
              ].map((option) => (
                <label key={option} className="flex items-center space-x-3 p-4 bg-white/40 backdrop-blur-sm rounded-xl border border-white/30 hover:bg-white/60 transition-all duration-200 cursor-pointer">
                  <input
                    type="radio"
                    name="relacionTecnologia"
                    value={option}
                    checked={formData.relacionTecnologia === option}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-green-500 focus:ring-green-400"
                  />
                  <span className="text-neutral-700" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>{option}</span>
                </label>
              ))}
            </div>
            {errors.relacionTecnologia && (
              <p className="text-red-500 text-sm flex items-center space-x-1" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>
                <span className="w-1 h-1 bg-red-400 rounded-full"></span>
                <span>{errors.relacionTecnologia}</span>
              </p>
            )}
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-neutral-700" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>
              ¿Con qué dispositivos trabajás habitualmente? *
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {dispositivosOptions.map((option) => (
                <label key={option} className="flex items-center space-x-3 p-4 bg-white/40 backdrop-blur-sm rounded-xl border border-white/30 hover:bg-white/60 transition-all duration-200 cursor-pointer">
                  <input
                    type="checkbox"
                    name="dispositivos"
                    value={option}
                    checked={formData.dispositivos.includes(option)}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-green-500 focus:ring-green-400 rounded"
                  />
                  <span className="text-neutral-700" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>{option}</span>
                </label>
              ))}
            </div>
            {errors.dispositivos && (
              <p className="text-red-500 text-sm flex items-center space-x-1" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>
                <span className="w-1 h-1 bg-red-400 rounded-full"></span>
                <span>{errors.dispositivos}</span>
              </p>
            )}
            {formData.dispositivos.includes('Otro') && (
              <div className="mt-2">
                <input
                  type="text"
                  name="otroDispositivo"
                  value={otroDispositivo}
                  onChange={(e) => {
                    const val = e.target.value.slice(0, 80);
                    setOtroDispositivo(val);
                    setFormData(prev => ({
                      ...prev,
                      dispositivos: upsertOtherInArray(prev.dispositivos, 'Otro', val)
                    }));
                  }}
                  className="w-full px-4 py-2 bg-white/60 backdrop-blur-sm border rounded-xl text-neutral-700 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent"
                  placeholder="Especificá otro dispositivo"
                />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-neutral-700" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>
              ¿Cuáles de estas herramientas ya usás o conocés? *
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {herramientasOptions.map((option) => (
                <label key={option} className="flex items-center space-x-3 p-4 bg-white/40 backdrop-blur-sm rounded-xl border border-white/30 hover:bg-white/60 transition-all duration-200 cursor-pointer">
                  <input
                    type="checkbox"
                    name="herramientas"
                    value={option}
                    checked={formData.herramientas.includes(option)}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-green-500 focus:ring-green-400 rounded"
                  />
                  <span className="text-neutral-700" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>{option}</span>
                </label>
              ))}
            </div>
            {formData.herramientas.includes('Otra') && (
              <div className="mt-2">
                <input
                  type="text"
                  name="otraHerramienta"
                  value={otraHerramienta}
                  onChange={(e) => {
                    const val = e.target.value.slice(0, 80);
                    setOtraHerramienta(val);
                    setFormData(prev => ({
                      ...prev,
                      herramientas: upsertOtherInArray(prev.herramientas, 'Otra', val)
                    }));
                  }}
                  className="w-full px-4 py-2 bg-white/60 backdrop-blur-sm border rounded-xl text-neutral-700 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent"
                  placeholder="Especificá otra herramienta"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Goals and Motivation */}
      <div className="relative overflow-hidden rounded-[2rem] bg-white/80 backdrop-blur-xl border border-white/50 shadow-2xl p-8">
        <div className="flex items-center space-x-3 mb-8">
          <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-pink-400 rounded-2xl flex items-center justify-center shadow-soft">
            <SparkleIcon className="w-6 h-6 text-white" />
          </div>
          <h3 className="text-2xl font-light text-neutral-700" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>
            Objetivos y Motivación
          </h3>
        </div>
        {/* Nota aclaratoria (empresa) */}
        {formData.isCompany && (
          <div className="mb-6">
            <div className="px-4 py-3 bg-gradient-to-r from-yellow-50 to-yellow-100 border border-yellow-200 rounded-xl text-sm text-neutral-700">
              Respondé esta sección pensando en tu empresa.
            </div>
          </div>
        )}
        
        <div className="space-y-6">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-neutral-700" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>
              Si tuvieras que elegir, ¿en qué te gustaría notar mejoras después de esta capacitación?
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {mejorasOptions.map((option) => (
                <label key={option} className="flex items-center space-x-3 p-4 bg-white/40 backdrop-blur-sm rounded-xl border border-white/30 hover:bg-white/60 transition-all duration-200 cursor-pointer">
                  <input
                    type="checkbox"
                    name="mejoras"
                    value={option}
                    checked={formData.mejoras.includes(option)}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-green-500 focus:ring-green-400 rounded"
                  />
                  <span className="text-neutral-700" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>{option}</span>
                </label>
              ))}
            </div>
            {/* mejoras es opcional; sin errores */}
            {formData.mejoras.includes('Otro') && (
              <div className="mt-2">
                <input
                  type="text"
                  name="otraMejora"
                  value={otraMejora}
                  onChange={(e) => {
                    const val = e.target.value.slice(0, 80);
                    setOtraMejora(val);
                    setFormData(prev => {
                      const next = upsertOtherInArray(prev.mejoras, 'Otro', val);
                      const dedup = Array.from(new Set(next));
                      return { ...prev, mejoras: dedup };
                    });
                  }}
                  className="w-full px-4 py-2 bg-white/60 backdrop-blur-sm border rounded-xl text-neutral-700 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent"
                  placeholder="Especificá otra mejora"
                />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-neutral-700" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>
              ¿Qué es lo que más te motiva sobre aprender a usar IA y herramientas digitales?
            </label>
            <textarea
              name="motivacion"
              value={formData.motivacion}
              onChange={handleInputChange}
              onFocus={() => setFocusedField('motivacion')}
              onBlur={() => setFocusedField('')}
              rows={4}
              maxLength={300}
              className={`w-full px-4 py-3 bg-white/60 backdrop-blur-sm border rounded-xl text-neutral-700 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent transition-all duration-300 resize-none ${
                errors.motivacion ? 'border-red-300 bg-red-50/50' : focusedField === 'motivacion' ? 'border-green-400 shadow-soft' : 'border-white/30'
              }`}
              placeholder="Compartí tu motivación..."
              style={{ fontFamily: 'Poppins, Inter, sans-serif' }}
            />
            <p className="text-xs text-neutral-500">Máximo 300 caracteres.</p>
            {/* motivación ahora opcional */}
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex justify-center">
        <button
          type="submit"
          disabled={
            isSubmitting ||
            Object.entries(errors).some(([k, v]) => k !== 'submit' && Boolean(v))
          }
          className={`px-12 py-4 bg-gradient-to-r from-green-400 to-blue-400 text-white font-medium rounded-2xl shadow-soft hover:shadow-medium transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none ${
            isSubmitting ? 'animate-pulse' : ''
          }`}
          style={{ fontFamily: 'Poppins, Inter, sans-serif' }}
        >
          {isSubmitting ? 'Enviando...' : 'Inscribirme en INICIA'}
        </button>
      </div>
    </form>
  );
};

export default RegistrationForm; 